/**
 * @providesModule @api/GetLeagues
 */
import RESTStore from 'RESTStore';
import {generateMockLeagueFromType, League, LeagueType} from './LeagueInfo';
import {NativeModules} from 'react-native';

type GetLeaguesVariables = {
  /**
   * Whether or not to show recently-concluded leagues instead of active ones. Default: false.
   */
  concluded?: boolean;
  /**
   * Limits the returned leagues to ones concluded after the specified date
   * @type {string} $iso-8601
   */
  ending_after?: string;
};

export type GetLeaguesResult = {
  /**
   * The list of league the user is segmented to
   */
  leagues: League[];
  /**
   * The default league id to show to the user (go to top tab of this league)
   */
  default_league_id: string;
};

export const GET_LEAGUES_ERRORS = {
  NO_LEAGUE_INSTANCE_ID: 'NO_LEAGUE_INSTANCE_ID',
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
  NOT_FOUND: 'NOT_FOUND',
  OTHER: 'OTHER',
} as const;

type GetLeaguesErrorName = (typeof GET_LEAGUES_ERRORS)[keyof typeof GET_LEAGUES_ERRORS];

export class GetLeaguesError extends Error {
  name: GetLeaguesErrorName;
  message: string;

  constructor({name, message}: {name: GetLeaguesErrorName; message: string}) {
    super();
    this.name = name;
    this.message = message;
  }
}

export const queryKeys = {
  getLeagues: ['getLeagues'],
  getConcludedLeagues: ['getConcludedLeagues'],
} as const;

/**
 * The get call to the Back-End to get the list of league the user is segmented to
 * @param variables {GetLeaguesVariables} the variable to pass to the API call
 * @see https://skillzsdk.docs.apiary.io/#reference/leagues/leagues/get-leagues
 * @returns a Promise to the list of leagues
 */
export const getLeagues = (variables: GetLeaguesVariables, needLeagueId = true) => {
  return new Promise(
    (resolve: (leagues: GetLeaguesResult) => void, reject: (error: GetLeaguesError) => void) => {
      RESTStore.getServerResponse(
        'engagement/leagues',
        variables as Record<string, string>,
        NativeModules.SKZAPIManager.OnError.failSilently,
        (response: GetLeaguesResult, e: unknown | null) => {
          if (e) {
            const error = e as {code: number};
            switch (error.code) {
              // eslint-disable-next-line no-magic-numbers
              case 500:
                reject(
                  new GetLeaguesError({
                    name: GET_LEAGUES_ERRORS.INTERNAL_SERVER_ERROR,
                    message: 'Internal server error',
                  })
                );
                break;
              // eslint-disable-next-line no-magic-numbers
              case 404:
                reject(
                  new GetLeaguesError({name: GET_LEAGUES_ERRORS.NOT_FOUND, message: 'Not found'})
                );
                break;

              // eslint-disable-next-line no-magic-numbers
              case 401:
                reject(
                  new GetLeaguesError({
                    name: GET_LEAGUES_ERRORS.UNAUTHORIZED,
                    message: 'Unauthorized',
                  })
                );
                break;

              default:
                reject(
                  new GetLeaguesError({name: GET_LEAGUES_ERRORS.OTHER, message: 'Other error'})
                );
                break;
            }
          } else {
            const hasLeagues = response.leagues?.length > 0;
            const hasLeagueInstanceId = response.leagues.every(
              (league) => league?.league_instance_id
            );
            const isConcluded = variables.concluded;

            if (hasLeagues && !hasLeagueInstanceId && !isConcluded && needLeagueId) {
              /** This can happen when is the first time the user is playing and the backend has not yet created a league instance for the user
               * So we reject and let the app handle it with a retry util the backend has created a league instance for the user.
               */
              reject(
                new GetLeaguesError({
                  name: GET_LEAGUES_ERRORS.NO_LEAGUE_INSTANCE_ID,
                  message: 'No league instance id found in the active league',
                })
              );
            }

            resolve(response);
          }
        }
      );
    }
  );
};

export const mockGetLeaguesResult: GetLeaguesResult = {
  leagues: [
    //practice
    generateMockLeagueFromType(LeagueType.Z),
    //pro
    generateMockLeagueFromType(LeagueType.CASH),
    //level
    generateMockLeagueFromType(LeagueType.LEVEL),
    //custom
    generateMockLeagueFromType(LeagueType.CUSTOM),
    //ticketz
    generateMockLeagueFromType(LeagueType.TICKETZ),
  ],
  default_league_id: '123',
};

export const mockGetLeaguesThemedResult: GetLeaguesResult = {
  leagues: [
    //practice not themed
    generateMockLeagueFromType(LeagueType.Z, {
      league_set_configuration_id: '1',
      league_id: '11',
      primary_color: undefined,
      secondary_color: undefined,
      additional_fields: undefined,
    }),
    //pro only primary themed
    generateMockLeagueFromType(LeagueType.CASH, {
      league_set_configuration_id: '2',
      league_id: '12',
      primary_color: 'blue',
      secondary_color: undefined,
    }),
    //level only mode light
    generateMockLeagueFromType(LeagueType.LEVEL, {
      league_set_configuration_id: 'LEVEL3',
      league_id: 'L1',
      title: 'Level light',
      primary_color: undefined,
      secondary_color: undefined,
      additional_fields: {league_theme_mode: 'light'},
    }),
    //level only mode dark
    generateMockLeagueFromType(LeagueType.LEVEL, {
      league_set_configuration_id: 'LEVEL4',
      league_id: 'L2',
      title: 'Level dark',
      primary_color: undefined,
      secondary_color: undefined,
      additional_fields: {league_theme_mode: 'dark'},
    }),
    //custom
    generateMockLeagueFromType(LeagueType.CUSTOM, {
      league_set_configuration_id: '5',
      league_id: '15',
      primary_color: 'red',
      secondary_color: 'pink',
      additional_fields: {league_theme_mode: 'dark'},
    }),
    //ticketz light
    generateMockLeagueFromType(LeagueType.TICKETZ, {
      league_set_configuration_id: '6',
      league_id: '16',
      title: 'Ticket light',
      primary_color: undefined,
      secondary_color: undefined,
      additional_fields: {league_theme_mode: 'light'},
    }),
    //ticketz dark
    generateMockLeagueFromType(LeagueType.TICKETZ, {
      league_set_configuration_id: '7',
      league_id: '17',
      title: 'Ticket dark',
      primary_color: undefined,
      secondary_color: undefined,
      additional_fields: {league_theme_mode: 'dark'},
    }),
    //ticketz primary and secondary
    generateMockLeagueFromType(LeagueType.TICKETZ, {
      league_set_configuration_id: '8',
      league_id: '18',
      title: 'Ticket colored',
      primary_color: 'green',
      secondary_color: 'red',
      additional_fields: undefined,
    }),
  ],
  default_league_id: '123',
};

export const mockGetConcludedLeaguesResult: GetLeaguesResult = {
  leagues: [
    //pro
    generateMockLeagueFromType(LeagueType.CASH, {league_id: '680'}),
    //level
    generateMockLeagueFromType(LeagueType.LEVEL, {league_id: 'L1'}),
  ],
  default_league_id: '123',
};
